import { GET_LIST_REQUEST_DEFAULT_LIMIT } from "constants/common";
import { useManageTable } from "hooks";
import { useMemo } from "react";
/* eslint-disable react-hooks/exhaustive-deps */
import { useParams, useSearchParams } from "react-router-dom";

import { useGetOrders } from "../../apis";
import {
  CLIENT_NAME_FILTER_SEARCH_PARAMETER,
  CLIENT_PHONE_FILTER_SEARCH_PARAMETER,
  CREATED_FROM_FILTER_SEARCH_PARAMETER,
  CREATED_TO_FILTER_SEARCH_PARAMETER,
  DELIVERY_LOCATION_FILTER_SEARCH_PARAMETER,
  DRIVER_ID_FILTER_SEARCH_PARAMETER,
  GENDER_FILTER_SEARCH_PARAMETER,
  LOCATION_ID_FILTER_SEARCH_PARAMETER,
  ORDER_TYPE_FILTER_SEARCH_PARAMETER,
  OrderType,
  RESELLER_NAME_FILTER_SEARCH_PARAMETER,
  RESELLER_PHONE_FILTER_SEARCH_PARAMETER,
  STATUS_FILTER_SEARCH_PARAMETER,
  TOTAL_FROM_FILTER_SEARCH_PARAMETER,
  TOTAL_TO_FILTER_SEARCH_PARAMETER,
  UNPACKING_STATUS_FILTER_SEARCH_PARAMETER
} from "../../constants";
import { OrdersFilterGenderValues } from "../../orders.types";
import useOrdersFilter from "../useOrdersFilter";
import { GetOrdersListRequestParams } from "./useOrdersList.types";

const useOrdersList = () => {
  const {
    page,
    searchValue,
    debouncedSearch,
    sorting,
    setSorting,
    setSearchValue,
    setPage
  } = useManageTable();

  const { drivers, isDriversLoading, locations, isLocationsLoading } =
    useOrdersFilter();

  const { resellerId } = useParams();
  const [searchParams] = useSearchParams();
  const selectedUnpackingStatus = searchParams.get(
    UNPACKING_STATUS_FILTER_SEARCH_PARAMETER
  );
  const selectedGender = searchParams.get(GENDER_FILTER_SEARCH_PARAMETER);
  const selectedTotalFrom = searchParams.get(
    TOTAL_FROM_FILTER_SEARCH_PARAMETER
  );
  const selectedTotalTo = searchParams.get(TOTAL_TO_FILTER_SEARCH_PARAMETER);
  const selectedStatus = searchParams.getAll(STATUS_FILTER_SEARCH_PARAMETER);
  const selectedLocation = searchParams.get(
    LOCATION_ID_FILTER_SEARCH_PARAMETER
  );
  const selectedDriver = searchParams.get(DRIVER_ID_FILTER_SEARCH_PARAMETER);
  const selectedClientName = searchParams.get(
    CLIENT_NAME_FILTER_SEARCH_PARAMETER
  );
  const selectedClientPhone = searchParams.get(
    CLIENT_PHONE_FILTER_SEARCH_PARAMETER
  );
  const selectedResellerName = searchParams.get(
    RESELLER_NAME_FILTER_SEARCH_PARAMETER
  );
  const selectedResellerPhone = searchParams.get(
    RESELLER_PHONE_FILTER_SEARCH_PARAMETER
  );
  const selectedDeliveryLocation = searchParams.get(
    DELIVERY_LOCATION_FILTER_SEARCH_PARAMETER
  );
  const selectedType = searchParams.get(ORDER_TYPE_FILTER_SEARCH_PARAMETER);
  const selectedCreatedFrom = searchParams.get(
    CREATED_FROM_FILTER_SEARCH_PARAMETER
  );
  const selectedCreatedTo = searchParams.get(
    CREATED_TO_FILTER_SEARCH_PARAMETER
  );

  const requestParams: GetOrdersListRequestParams = useMemo(
    () => ({
      page,
      take: GET_LIST_REQUEST_DEFAULT_LIMIT,
      search: debouncedSearch,
      orderBy: sorting[0]?.id ?? null,
      order: sorting[0] ? (sorting[0]?.desc ? "DESC" : "ASC") : "DESC",
      resellerId,
      unpackingStatus: !selectedUnpackingStatus
        ? undefined
        : selectedUnpackingStatus,
      gender:
        !selectedGender || selectedGender === OrdersFilterGenderValues.ALL
          ? undefined
          : selectedGender,
      orderTotalFrom:
        selectedTotalFrom === null ? null : Number(selectedTotalFrom),
      orderTotalTo: selectedTotalTo === null ? null : Number(selectedTotalTo),
      deliveryZoneId: selectedLocation?.length ? selectedLocation : undefined,
      statuses:
        selectedStatus?.length > 0 ? selectedStatus.join(",") : undefined,
      driverId: selectedDriver?.length ? selectedDriver : undefined,
      clientName: selectedClientName?.length ? selectedClientName : undefined,
      clientPhone: selectedClientPhone?.length
        ? selectedClientPhone
        : undefined,
      resellerName: selectedResellerName?.length
        ? selectedResellerName
        : undefined,
      resellerPhone: selectedResellerPhone?.length
        ? selectedResellerPhone
        : undefined,
      deliveryLocation: selectedDeliveryLocation?.length
        ? selectedDeliveryLocation
        : undefined,
      type:
        selectedType === OrderType.Normal ||
        selectedType === OrderType.Expedition
          ? selectedType
          : undefined,
      createdFrom: selectedCreatedFrom?.length ? selectedCreatedFrom : undefined,
      createdTo: selectedCreatedTo?.length ? selectedCreatedTo : undefined
    }),
    [
      page,
      debouncedSearch,
      sorting,
      searchParams,
      resellerId,
      selectedGender,
      selectedTotalFrom,
      selectedTotalTo,
      selectedLocation,
      selectedStatus,
      selectedClientName,
      selectedClientPhone,
      selectedResellerName,
      selectedResellerPhone,
      selectedDeliveryLocation,
      selectedType,
      selectedCreatedFrom,
      selectedCreatedTo
    ]
  );

  const {
    orders,
    meta,
    isLoading,
    refetch: refetchOrders
  } = useGetOrders(requestParams);

  return {
    orders,
    totalItems: meta?.itemCount ?? 0,
    searchValue,
    page,
    isLoading,
    sorting,
    setSorting,
    setSearchValue,
    setPage,
    refetchOrders,

    drivers,
    isDriversLoading,
    locations,
    isLocationsLoading
  };
};

export default useOrdersList;
